<script lang="ts">
/**
 * HubSpotForm - Dynamic form component that renders fields from a HubSpot form schema
 * Features:
 * - Renders form fields based on HubSpot form definition
 * - Optional Mailchimp newsletter subscription checkbox
 * - reCAPTCHA v3 spam protection
 * - Analytics tracking integration
 */
import { browser } from '$app/environment';
import { page } from '$app/state';
import { trackOpenAILeadCreated } from '$lib/services/openai-ads.js';
import type { HubSpotField, HubSpotFieldGroup, HubSpotFormSchema } from '$lib/types/hubspot-form';
import { EVENTS, identifyUser, trackEvent } from '$lib/utils/analytics';
import { getCookie } from '$lib/utils/cookies';
import { sanitiseHubspotSuccessMessage } from '$lib/utils/hubspotSuccessMessage.js';
import { type GRecaptcha, loadRecaptcha } from '$lib/utils/recaptcha-client';
import { RECAPTCHA_ACTIONS } from '$lib/utils/recaptchaActions';
import { GOOGLE_RECAPTCHA_SITE_KEY } from '../../../config/apiKeys';
import FormSelect from '../../atoms/FormSelect/FormSelect.svelte';
import HtmlText from '../../atoms/HtmlText/HtmlText.svelte';
import InputContainer from '../InputContainer/InputContainer.svelte';

interface Props {
    formId: string;
    formSchema?: HubSpotFormSchema | null;
    portalId?: string;
    fieldLayout?: 'single-column' | 'double-column';
    showMailchimpCheckbox?: boolean;
    mailchimpCheckboxLabel?: string;
    successMessage?: string;
    submitButtonText?: string;
    conditionsText?: string;
    className?: string;
    replaceContainerOnSuccess?: boolean;
    hideBlock?: boolean;
    onSuccess?: () => void;
}

interface HubSpotSubmitResponse {
    success: boolean;
    error?: string;
    inlineMessage?: string | null;
    bypassedNotifications?: boolean;
}

let {
    formId,
    formSchema = null,
    portalId = '',
    fieldLayout = 'double-column',
    showMailchimpCheckbox = false,
    mailchimpCheckboxLabel = 'Subscribe to our newsletter',
    successMessage = '<p>Thank you for your submission!</p>',
    submitButtonText = 'Submit',
    conditionsText = '',
    className = '',
    replaceContainerOnSuccess = false,
    hideBlock = false,
    onSuccess,
}: Props = $props();

// Try to get schema from page data if not provided directly
let schema = $derived(formSchema || page.data?.hubspotFormSchemas?.[formId]);

// Compute field rows using smart pairing (moved here as derived to avoid @const placement issues)
let fieldRows = $derived.by(() => {
    if (!schema?.fieldGroups) return [];
    const allFields = schema.fieldGroups.flatMap((g: HubSpotFieldGroup) => g.fields || []);
    return groupFieldsIntoRows(allFields, fieldLayout);
});

// Form state
let formValues: Record<string, string> = $state({});
let submitting = $state(false);
let submitted = $state(false);
let error = $state('');
let subscribeToNewsletter = $state(false);
let formStarted = $state(false);
let successMessageHtml = $derived(sanitiseHubspotSuccessMessage(successMessage));

// Initialise form values when schema is available
$effect(() => {
    if (schema && Object.keys(formValues).length === 0) {
        console.log(`[HubSpot Form] Initialising form values for: ${formId}`);
        const values: Record<string, string> = {};

        schema.fieldGroups?.forEach((group: HubSpotFieldGroup) => {
            group.fields?.forEach((field: HubSpotField) => {
                if (!field.hidden) {
                    values[field.name] = field.defaultValue || '';
                }
            });
        });

        formValues = values;
        console.log(`[HubSpot Form] Form values initialised:`, Object.keys(values));
    }
});

// Log component mount
$effect(() => {
    if (browser) {
        console.log(`[HubSpot Form] Component mounted for form: ${formId}`);
        console.log(`[HubSpot Form] Schema loaded: ${schema ? '✓ Yes' : '✗ No'}`);
        console.log(`[HubSpot Form] replaceContainerOnSuccess: ${replaceContainerOnSuccess}`);
        if (schema) {
            console.log(`[HubSpot Form] Form name: "${schema.name}"`);
            const fieldCount =
                schema.fieldGroups?.reduce(
                    (sum: number, g: HubSpotFieldGroup) => sum + (g.fields?.length || 0),
                    0,
                ) || 0;
            console.log(`[HubSpot Form] Field count: ${fieldCount}`);
        }
    }
});

/**
 * Strip HTML tags from a string (HubSpot labels often contain HTML)
 */
function stripHtml(html: string): string {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '');
}

/**
 * Map HubSpot field types to InputContainer/FormSelect types
 */
function mapFieldType(hubspotType: string): string {
    const typeMap: Record<string, string> = {
        email: 'email',
        phone: 'tel',
        phonenumber: 'tel',
        single_line_text: 'text',
        multi_line_text: 'textarea',
        dropdown: 'select',
        text: 'text',
    };
    return typeMap[hubspotType] || 'text';
}

/**
 * Group fields into rows using smart pairing defaults
 * Since HubSpot API doesn't expose layout, we use common field name patterns
 */
function groupFieldsIntoRows(
    fields: HubSpotField[],
    fieldLayout: Props['fieldLayout'],
): HubSpotField[][] {
    const visibleFields = fields.filter((f) => !f.hidden);
    const rows: HubSpotField[][] = [];
    const used = new Set<string>();

    if (fieldLayout === 'double-column') {
        // Define field pairs (order matters - first field appears on left)
        const pairs: [string, string][] = [
            ['firstname', 'lastname'],
            ['email', 'phone'],
            ['company', 'job_title_dropdown'],
            ['company', 'jobtitle'],
            ['company', 'job_title'],
        ];

        // Try to create pairs
        for (const [left, right] of pairs) {
            const leftField = visibleFields.find(
                (f) => f.name.toLowerCase() === left && !used.has(f.name),
            );
            const rightField = visibleFields.find(
                (f) => f.name.toLowerCase() === right && !used.has(f.name),
            );

            if (leftField && rightField) {
                rows.push([leftField, rightField]);
                used.add(leftField.name);
                used.add(rightField.name);
            }
        }
    }

    // Add remaining fields as single-column rows (preserving original order)
    for (const field of visibleFields) {
        if (!used.has(field.name)) {
            rows.push([field]);
        }
    }

    return rows;
}

/**
 * Track form start (only once per form interaction)
 */
function handleFormStart() {
    if (browser && !formStarted) {
        formStarted = true;
        console.log('[HubSpot Form] Form interaction started');
        trackEvent(EVENTS.CONTACT_FORM_STARTED, {
            form_name: schema?.name || formId,
            form_id: formId,
            page_url: window.location.href,
        });
    }
}

/**
 * Execute reCAPTCHA and return token
 */
async function executeRecaptcha(): Promise<string | null> {
    console.log('[HubSpot Form] Executing reCAPTCHA...');

    let recaptcha: GRecaptcha;
    try {
        recaptcha = await loadRecaptcha(GOOGLE_RECAPTCHA_SITE_KEY);
    } catch (err) {
        console.error('[HubSpot Form] reCAPTCHA script failed to load:', err);
        return null;
    }

    return new Promise((resolve) => {
        recaptcha.ready(() => {
            recaptcha
                .execute(GOOGLE_RECAPTCHA_SITE_KEY, { action: RECAPTCHA_ACTIONS.hubspot })
                .then((token: string) => {
                    console.log('[HubSpot Form] ✓ reCAPTCHA token obtained');
                    resolve(token);
                })
                .catch((err: Error) => {
                    console.error('[HubSpot Form] ✗ reCAPTCHA execution failed:', err);
                    resolve(null);
                });
        });
    });
}

/**
 * Handle form submission
 */
async function handleSubmit(event: SubmitEvent) {
    event.preventDefault();

    console.log('[HubSpot Form] === Form Submission Started ===');
    console.log('[HubSpot Form] Form ID:', formId);
    console.log('[HubSpot Form] Field count:', Object.keys(formValues).length);
    console.log('[HubSpot Form] Newsletter opt-in:', subscribeToNewsletter);

    submitting = true;
    error = '';

    // Execute reCAPTCHA
    const token = await executeRecaptcha();
    if (!token) {
        error = 'Security verification failed. Please refresh and try again.';
        submitting = false;
        trackEvent(EVENTS.FORM_ERROR, {
            form_name: schema?.name || formId,
            form_id: formId,
            error: 'recaptcha_failed',
        });
        return;
    }

    try {
        console.log('[HubSpot Form] Submitting to API...');

        // Get HubSpot tracking cookie if available (only exists after user accepts analytics cookies)
        const hutk = getCookie('hubspotutk');

        const response = await fetch('/api/hubspot-form/submit', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                formId,
                portalId,
                fields: formValues,
                subscribeToNewsletter: showMailchimpCheckbox && subscribeToNewsletter,
                recaptchaToken: token,
                hutk, // HubSpot tracking cookie for contact association (null if not consented)
                context: {
                    pageUri: window.location.href,
                    pageName: document.title,
                },
            }),
        });

        const result = (await response.json()) as HubSpotSubmitResponse;

        if (response.ok && result.success) {
            console.log('[HubSpot Form] ✓ Submission successful');
            submitted = true;

            // Track successful submission
            trackEvent(EVENTS.CONTACT_FORM_SUBMITTED, {
                form_name: schema?.name || formId,
                form_id: formId,
                email: formValues.email,
                page_url: window.location.href,
                newsletter_optin: subscribeToNewsletter,
            });
            if (!result.bypassedNotifications) {
                trackOpenAILeadCreated();
            }

            // Identify user if email provided
            if (formValues.email) {
                const nameParts = (formValues.firstname || formValues.name || '').split(' ');
                identifyUser(formValues.email, {
                    firstname: formValues.firstname || nameParts[0] || '',
                    lastname: formValues.lastname || nameParts.slice(1).join(' ') || '',
                    phone: formValues.phone || formValues.telephone || '',
                    hubspot_form_submitted: true,
                    hubspot_form_id: formId,
                    submission_date: new Date().toISOString(),
                });
            }

            try {
                onSuccess?.();
            } catch (err) {
                console.warn('[HubSpot Form] onSuccess callback error:', err);
            }

            // Replace parent container content with success message if enabled
            console.log('[HubSpot Form] replaceContainerOnSuccess:', replaceContainerOnSuccess);
            if (replaceContainerOnSuccess) {
                const formElement = document.querySelector(
                    `.hubspot-form[data-form-id="${formId}"]`,
                );
                console.log('[HubSpot Form] Form element found:', formElement);
                const container = formElement?.closest('.container');
                console.log('[HubSpot Form] Container found:', container);

                if (container) {
                    console.log('[HubSpot Form] Replacing container content');
                    container.innerHTML = `<div class="builder-text">${successMessageHtml}</div>`;
                } else {
                    console.warn('[HubSpot Form] No .container ancestor found');
                }
            }
        } else {
            console.error('[HubSpot Form] ✗ Submission failed:', result.error);
            error = result.error || 'Something went wrong. Please try again.';

            trackEvent(EVENTS.FORM_ERROR, {
                form_name: schema?.name || formId,
                form_id: formId,
                error: result.error || 'submission_failed',
            });
        }
    } catch (err) {
        console.error('[HubSpot Form] ✗ Network error:', err);
        error = 'Network error. Please check your connection and try again.';

        trackEvent(EVENTS.FORM_ERROR, {
            form_name: schema?.name || formId,
            form_id: formId,
            error: 'network_error',
        });
    } finally {
        submitting = false;
        console.log('[HubSpot Form] === Form Submission Complete ===');
    }
}
</script>

{#if !hideBlock}
    <div class="hubspot-form {className}" data-form-id={formId}>
        {#if !schema}
            <p class="hubspot-form__error">
                Form configuration not found. Please check the form ID.
            </p>
        {:else if submitted}
            <HtmlText text={successMessageHtml} />
        {:else if Object.keys(formValues).length === 0}
            <!-- Skeleton loading state while formValues initialises -->
            <div class="hubspot-form__skeleton">
                {#each fieldRows as row}
                    <div
                        class="hubspot-form__row"
                        class:hubspot-form__row--multi={row.length > 1}
                    >
                        {#each row as _field}
                            <div class="hubspot-form__skeleton-field">
                                <div class="hubspot-form__skeleton-label"></div>
                                <div class="hubspot-form__skeleton-input"></div>
                            </div>
                        {/each}
                    </div>
                {/each}
                <div class="hubspot-form__skeleton-button"></div>
            </div>
        {:else}
            {#if error}
                <p class="hubspot-form__error">{error}</p>
            {/if}

            <form onsubmit={handleSubmit} class="hubspot-form__form">
                {#each fieldRows as row}
                    <div
                        class="hubspot-form__row"
                        class:hubspot-form__row--multi={row.length > 1}
                    >
                        {#each row as field}
                            {@const fieldType = mapFieldType(field.fieldType)}
                            {@const fieldLabel =
                                stripHtml(field.label) +
                                (field.required ? "*" : "")}

                            {#if fieldType === "select"}
                                <FormSelect
                                    label={fieldLabel}
                                    name={field.name}
                                    id={field.name}
                                    placeholder={field.placeholder ||
                                        `Select ${stripHtml(field.label)}`}
                                    options={field.options || []}
                                    required={field.required}
                                    bind:value={formValues[field.name]}
                                    onfocus={handleFormStart}
                                />
                            {:else}
                                <InputContainer
                                    type={fieldType}
                                    name={field.name}
                                    id={field.name}
                                    placeholder={fieldLabel}
                                    required={field.required}
                                    role=""
                                    customValidationMessage=""
                                    bind:valueObject={formValues[field.name]}
                                    onfocus={handleFormStart}
                                />
                            {/if}
                        {/each}
                    </div>
                {/each}

                {#if showMailchimpCheckbox}
                    <label class="hubspot-form__checkbox">
                        <input
                            type="checkbox"
                            bind:checked={subscribeToNewsletter}
                        />
                        <span>{mailchimpCheckboxLabel}</span>
                    </label>
                {/if}

                {#if conditionsText}
                    <p class="hubspot-form__conditions">{conditionsText}</p>
                {/if}

                <button
                    type="submit"
                    class="button button--inverse hubspot-form__submit"
                    disabled={submitting}
                >
                    {submitting ? "Submitting..." : submitButtonText}
                </button>
            </form>
        {/if}
    </div>
{/if}

<style lang="scss">
    @use "/src/scss/global-constants/fonts";
    @use "/src/scss/global-constants/typography";
    @use "sass-mq/mq" as *;

    .hubspot-form {
        padding: 0 var(--layout-pagegrid-margin);
        margin: 0 auto;
        max-width: 93ch;

        &__error {
            color: var(--colour-sacrifice-altar);
            font-family: fonts.$fonts-secondary;
            font-size: 1.4rem;
            line-height: 1.4;
            margin-bottom: 2rem;
        }

        &__conditions {
            color: var(--colour-dreamless-sleep);
            font-family: fonts.$fonts-secondary;
            font-size: 1.4rem;
            font-style: italic;
            line-height: 1.6;
            margin-bottom: 2.4rem;
        }

        &__checkbox {
            align-items: flex-start;
            cursor: pointer;
            display: flex;
            font-family: fonts.$fonts-secondary;
            font-size: 1.4rem;
            gap: 1rem;
            line-height: 1.4;
            margin-bottom: 2.4rem;

            input[type="checkbox"] {
                accent-color: var(--colour-dreamless-sleep);
                cursor: pointer;
                flex-shrink: 0;
                height: 1.8rem;
                margin-top: 0.2rem;
                width: 1.8rem;
            }

            span {
                color: var(--colour-dreamless-sleep);
            }
        }

        &__form {
            display: flex;
            flex-direction: column;

            :global(.input-container),
            :global(.field) {
                width: 100%;
            }
        }

        &__row {
            display: grid;
            gap: 0 2rem;
            grid-template-columns: 1fr;

            @include mq($from: tablet) {
                &--multi {
                    grid-template-columns: repeat(auto-fit, minmax(20rem, 1fr));
                }
            }
        }

        &__skeleton {
            display: flex;
            flex-direction: column;
        }

        &__skeleton-field {
            margin-bottom: 3rem;

            @include mq($from: tablet) {
                margin-bottom: 4rem;
            }
        }

        &__skeleton-label {
            animation: skeleton-shimmer 1.5s infinite;
            background: linear-gradient(
                90deg,
                var(--colour-errigal-white) 25%,
                var(--colour-white) 50%,
                var(--colour-errigal-white) 75%
            );
            background-size: 200% 100%;
            border-radius: 2px;
            height: 1.1rem;
            margin-bottom: 0.8rem;
            width: 8rem;
        }

        &__skeleton-input {
            animation: skeleton-shimmer 1.5s infinite;
            background: linear-gradient(
                90deg,
                var(--colour-errigal-white) 25%,
                var(--colour-white) 50%,
                var(--colour-errigal-white) 75%
            );
            background-size: 200% 100%;
            border-bottom: 1px solid var(--colour-squant);
            height: 2.6rem;
            width: 100%;
        }

        &__skeleton-button {
            align-self: flex-end;
            animation: skeleton-shimmer 1.5s infinite;
            background: linear-gradient(
                90deg,
                var(--colour-errigal-white) 25%,
                var(--colour-white) 50%,
                var(--colour-errigal-white) 75%
            );
            background-size: 200% 100%;
            border-radius: 100rem;
            height: 4rem;
            width: 12rem;
        }

        &__submit {
            align-self: flex-end;
            @include typography.label-button;
            background: var(--colour-dreamless-sleep);
            border-radius: 100rem;
            border: none;
            color: var(--colour-white);
            cursor: pointer;
            padding: 1rem 3.5rem 1rem 1.5rem;
            position: relative;
            text-decoration: none;
            text-transform: uppercase;
            overflow: hidden;

            &::after {
                background: url(/src/stories/assets/arrow-right-white.svg)
                    repeat-x left center;
                content: " ";
                height: 100%;
                position: absolute;
                right: 1.5rem;
                top: 0;
                width: 0.7rem;
            }

            &:hover:not(:disabled) {
                &::after {
                    animation: arrowShift var(--timing-duration-long)
                        ease-in-out forwards;
                }
            }

            &:disabled {
                cursor: not-allowed;
                opacity: 0.6;
            }
        }
    }

    @keyframes arrowShift {
        from {
            background-position-x: left;
        }
        to {
            background-position-x: 0.7rem;
        }
    }

    @keyframes skeleton-shimmer {
        0% {
            background-position: 200% 0;
        }
        100% {
            background-position: -200% 0;
        }
    }
</style>
